import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import type { ReferralCampaign } from '@/types/referral';
import type { ReferralProgram } from '@/types/referral';

export interface EditReferralFlowFormData {
  name: string;
  description: string;
  status: string;
  program_id: string;
  flow_type: 'automatic' | 'manual';
  notes: string;
  variant_label: string;
}

interface EditReferralEmailFlowDialogContextType {
  isOpen: boolean;
  campaign: ReferralCampaign | null;
  formData: EditReferralFlowFormData;
  setFormData: React.Dispatch<React.SetStateAction<EditReferralFlowFormData>>;
  programs: ReferralProgram[];
  openEditFlow: (campaign: ReferralCampaign, programs: ReferralProgram[], onSave: (id: string, updates: Partial<ReferralCampaign>) => Promise<ReferralCampaign | null>) => void;
  closeEditFlow: () => void;
  onSave: ((id: string, updates: Partial<ReferralCampaign>) => Promise<ReferralCampaign | null>) | null;
}

const defaultFormData: EditReferralFlowFormData = {
  name: '',
  description: '',
  status: 'draft',
  program_id: '',
  flow_type: 'automatic',
  notes: '',
  variant_label: '',
};

const EditReferralEmailFlowDialogContext = createContext<EditReferralEmailFlowDialogContextType | undefined>(undefined);

export function EditReferralEmailFlowDialogProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [campaign, setCampaign] = useState<ReferralCampaign | null>(null);
  const [formData, setFormData] = useState<EditReferralFlowFormData>(defaultFormData);
  const [programs, setPrograms] = useState<ReferralProgram[]>([]);
  const [onSave, setOnSave] = useState<((id: string, updates: Partial<ReferralCampaign>) => Promise<ReferralCampaign | null>) | null>(null);

  const openEditFlow = useCallback(
    (c: ReferralCampaign, progs: ReferralProgram[], saveFn: (id: string, updates: Partial<ReferralCampaign>) => Promise<ReferralCampaign | null>) => {
      setCampaign(c);
      setFormData({
        name: c.name,
        description: c.description ?? '',
        status: c.status,
        program_id: c.program_id ?? '',
        flow_type: (c.flow_type ?? 'automatic') as 'automatic' | 'manual',
        notes: c.notes ?? '',
        variant_label: c.variant_label ?? '',
      });
      setPrograms(progs);
      setOnSave(() => saveFn);
      setIsOpen(true);
    },
    []
  );

  const closeEditFlow = useCallback(() => {
    setIsOpen(false);
    setCampaign(null);
    setOnSave(null);
  }, []);

  const value: EditReferralEmailFlowDialogContextType = {
    isOpen,
    campaign,
    formData,
    setFormData,
    programs,
    openEditFlow,
    closeEditFlow,
    onSave,
  };

  return (
    <EditReferralEmailFlowDialogContext.Provider value={value}>
      {children}
    </EditReferralEmailFlowDialogContext.Provider>
  );
}

export function useEditReferralEmailFlowDialog() {
  const ctx = useContext(EditReferralEmailFlowDialogContext);
  if (ctx === undefined) throw new Error('useEditReferralEmailFlowDialog must be used within EditReferralEmailFlowDialogProvider');
  return ctx;
}
